import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Text, HStack, Flex, Button, IconButton, VStack } from '@chakra-ui/react';
import { AddIcon, CloseIcon } from '@chakra-ui/icons';
import { MdDragIndicator } from 'react-icons/md';
import type { LocalCreatorsPackageRow } from '../../types';
import { ff } from '../../../../../../theme';
import { reorderListItems } from '../../lib/reorderListItems';
import PackageComposition from '../package/PackageComposition';
import { Pill } from '../../../../../../components/ui';

export interface PackagesSectionProps {
  packages: LocalCreatorsPackageRow[];
  onRemovePackage: (id: string) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onNewPackage: () => void;
  onEditPackage?: (pkg: LocalCreatorsPackageRow) => void;
}

const PACKAGE_DIVIDER = `1px solid ${ff.line}`;
const DRAG_CLICK_THRESHOLD_PX = 8;

function findRowIndexAtClientY(clientY: number, refs: (HTMLElement | null)[]): number | null {
  for (let i = 0; i < refs.length; i++) {
    const el = refs[i];
    if (!el) continue;
    const rect = el.getBoundingClientRect();
    if (clientY >= rect.top && clientY <= rect.bottom) {
      return i;
    }
  }
  return null;
}

const PackagesSection: React.FC<PackagesSectionProps> = ({
  packages,
  onRemovePackage,
  onReorder,
  onNewPackage,
  onEditPackage,
}) => {
  const [previewPackages, setPreviewPackages] = useState<LocalCreatorsPackageRow[] | null>(null);
  const [dragSessionActive, setDragSessionActive] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);

  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const didDragRef = useRef(false);
  const dragEndedRef = useRef(false);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const dragSourceIndexRef = useRef<number | null>(null);
  const suppressRowClickRef = useRef(false);

  const canReorder = packages.length >= 2;
  const displayPackages = previewPackages ?? packages;

  const resetDrag = useCallback(() => {
    setPreviewPackages(null);
    setIsDragging(false);
    setDragSessionActive(false);
    setDraggedId(null);
    dragStartRef.current = null;
    dragSourceIndexRef.current = null;
    didDragRef.current = false;
    dragEndedRef.current = false;
  }, []);

  const applyPreviewAtTarget = useCallback(
    (targetIndex: number, activeDraggedId: string) => {
      setPreviewPackages((prev) => {
        const base = prev ?? packages;
        const fromIndex = base.findIndex((p) => p.id === activeDraggedId);
        if (fromIndex < 0 || fromIndex === targetIndex) {
          return prev;
        }
        return reorderListItems(base, fromIndex, targetIndex);
      });
    },
    [packages]
  );

  const finishDrag = useCallback(
    (commit: boolean) => {
      if (dragEndedRef.current) return;
      dragEndedRef.current = true;

      const fromIndex = dragSourceIndexRef.current;
      if (commit && didDragRef.current && fromIndex !== null && draggedId !== null) {
        suppressRowClickRef.current = true;
        const slots = previewPackages ?? packages;
        const toIndex = slots.findIndex((p) => p.id === draggedId);
        if (toIndex >= 0 && fromIndex !== toIndex) {
          onReorder(fromIndex, toIndex);
        }
      }
      resetDrag();
    },
    [draggedId, onReorder, packages, previewPackages, resetDrag]
  );

  const handlePointerMove = useCallback(
    (clientX: number, clientY: number) => {
      if (dragSourceIndexRef.current === null || !dragStartRef.current) return;

      const dx = clientX - dragStartRef.current.x;
      const dy = clientY - dragStartRef.current.y;
      if (!didDragRef.current && Math.hypot(dx, dy) >= DRAG_CLICK_THRESHOLD_PX) {
        didDragRef.current = true;
        setIsDragging(true);
      }

      if (!didDragRef.current || draggedId === null) return;

      const target = findRowIndexAtClientY(clientY, rowRefs.current);
      if (target !== null) {
        applyPreviewAtTarget(target, draggedId);
      }
    },
    [applyPreviewAtTarget, draggedId]
  );

  const handleDragHandlePointerDown = useCallback(
    (index: number, e: React.PointerEvent<HTMLDivElement>) => {
      if (!canReorder) return;
      e.stopPropagation();
      e.preventDefault();

      dragEndedRef.current = false;
      didDragRef.current = false;
      dragStartRef.current = { x: e.clientX, y: e.clientY };
      dragSourceIndexRef.current = index;
      setDraggedId(displayPackages[index]?.id ?? null);
      setDragSessionActive(true);
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [canReorder, displayPackages]
  );

  useEffect(() => {
    if (!dragSessionActive) return;

    const onWindowPointerMove = (e: PointerEvent) => {
      handlePointerMove(e.clientX, e.clientY);
    };
    const onWindowPointerUp = () => {
      finishDrag(didDragRef.current);
    };

    window.addEventListener('pointermove', onWindowPointerMove);
    window.addEventListener('pointerup', onWindowPointerUp);
    window.addEventListener('pointercancel', onWindowPointerUp);

    return () => {
      window.removeEventListener('pointermove', onWindowPointerMove);
      window.removeEventListener('pointerup', onWindowPointerUp);
      window.removeEventListener('pointercancel', onWindowPointerUp);
    };
  }, [dragSessionActive, finishDrag, handlePointerMove]);

  const handleRowClick = (pkg: LocalCreatorsPackageRow) => {
    if (suppressRowClickRef.current) {
      suppressRowClickRef.current = false;
      return;
    }
    if (isDragging) return;
    onEditPackage?.(pkg);
  };

  return (
    <Box as="section" aria-label="Packages">
      <Text as="h3" textStyle="eyebrow" color={ff.text2} mb="10px">
        Packages
      </Text>
      <Box
        border="1px solid"
        borderColor={ff.line}
        borderRadius="20px"
        overflow="hidden"
        bg="white"
      >
        {displayPackages.map((pkg, index) => {
          const featured = Boolean(pkg.featured);
          const desc = pkg.description.trim();
          const hasBodyDesc = desc.length > 0;
          const hasComposition = pkg.deliverables.length > 0;
          const isDraggedRow = isDragging && pkg.id === draggedId;

          return (
            <HStack
              key={pkg.id}
              ref={(el) => {
                rowRefs.current[index] = el;
              }}
              align="center"
              spacing={0}
              borderTop={index > 0 ? PACKAGE_DIVIDER : 'none'}
              position="relative"
              bg="white"
              py="14px"
              pl={0}
              pr="6px"
              minH="60px"
              opacity={isDraggedRow ? 0.55 : 1}
              transition={isDragging ? 'none' : 'opacity 0.15s'}
              cursor={onEditPackage ? 'pointer' : undefined}
              onClick={onEditPackage ? () => handleRowClick(pkg) : undefined}
              onKeyDown={
                onEditPackage
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRowClick(pkg);
                      }
                    }
                  : undefined
              }
              role={onEditPackage ? 'button' : undefined}
              tabIndex={onEditPackage ? 0 : undefined}
              aria-label={
                onEditPackage
                  ? `Edit package ${pkg.price}${hasBodyDesc ? ', tap to view full description' : ''}`
                  : undefined
              }
            >
              {featured && (
                <Box
                  w="3px"
                  position="absolute"
                  left={0}
                  top="8px"
                  bottom="8px"
                  borderRadius="full"
                  bg={ff.lime}
                  pointerEvents="none"
                />
              )}
              {canReorder ? (
                <Flex
                  w="36px"
                  minW="36px"
                  flexShrink={0}
                  align="center"
                  justify="center"
                  alignSelf="stretch"
                  pl="4px"
                  color={ff.muted}
                  cursor="grab"
                  userSelect="none"
                  sx={{ touchAction: 'none' }}
                  onPointerDown={(e) => handleDragHandlePointerDown(index, e)}
                >
                  <Box transform="scale(0.9)" pointerEvents="none">
                    <MdDragIndicator size={20} />
                  </Box>
                </Flex>
              ) : null}
              <Box flex={1} minW={0} pl={canReorder ? '2px' : '16px'} pr="4px">
                <VStack align="stretch" spacing={0} minW={0}>
                  {featured && (
                    <Box mb="8px">
                      <Pill tone="lime">Most popular</Pill>
                    </Box>
                  )}
                  {hasComposition && (
                    <HStack
                      align="center"
                      justify="space-between"
                      spacing="12px"
                      w="100%"
                      pr="4px"
                    >
                      <Box flex={1} minW={0}>
                        <PackageComposition deliverables={pkg.deliverables} />
                      </Box>
                      <Text
                        flexShrink={0}
                        textStyle="display"
                        fontSize="20px"
                        color={ff.ink}
                        textAlign="right"
                      >
                        {pkg.price}
                      </Text>
                    </HStack>
                  )}
                  {!hasComposition && hasBodyDesc && (
                    <HStack
                      align="center"
                      justify="space-between"
                      spacing="12px"
                      w="100%"
                      pr="4px"
                    >
                      <Text
                        flex={1}
                        minW={0}
                        fontSize="14px"
                        lineHeight="1.4"
                        color={ff.text2}
                        noOfLines={3}
                      >
                        {desc}
                      </Text>
                      <Text
                        flexShrink={0}
                        textStyle="display"
                        fontSize="20px"
                        color={ff.ink}
                        textAlign="right"
                      >
                        {pkg.price}
                      </Text>
                    </HStack>
                  )}
                  {hasComposition && hasBodyDesc && (
                    <Text
                      mt="10px"
                      pr="2px"
                      fontSize="14px"
                      lineHeight="1.4"
                      color={ff.text2}
                      noOfLines={3}
                    >
                      {desc}
                    </Text>
                  )}
                  {!hasComposition && !hasBodyDesc && (
                    <HStack justify="flex-end" w="100%" pr="4px">
                      <Text
                        textStyle="display"
                        fontSize="20px"
                        color={ff.ink}
                      >
                        {pkg.price}
                      </Text>
                    </HStack>
                  )}
                </VStack>
              </Box>
              <IconButton
                flexShrink={0}
                alignSelf="center"
                aria-label="Remove package"
                icon={
                  <Box
                    as="span"
                    opacity={0.45}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    w="16px"
                    h="16px"
                  >
                    <CloseIcon boxSize="8px" />
                  </Box>
                }
                w="44px"
                h="44px"
                minW="44px"
                borderRadius="full"
                color={ff.ink}
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemovePackage(pkg.id);
                }}
                _hover={{ bg: ff.grey }}
              />
            </HStack>
          );
        })}
      </Box>
      <Button
        type="button"
        w="100%"
        h="56px"
        mt="10px"
        borderRadius="20px"
        border="1.5px dashed"
        borderColor={ff.border}
        bg={ff.grey}
        color={ff.ink}
        fontFamily="display"
        fontSize="15px"
        fontWeight={600}
        leftIcon={<AddIcon boxSize="12px" color={ff.ink} aria-hidden />}
        onClick={onNewPackage}
        _hover={{ borderColor: ff.ink }}
      >
        Add package
      </Button>
    </Box>
  );
};

export default PackagesSection;
